import type {
	CreateFlagRequest,
	FlagDoc,
	FlagStatus,
	UpdateFlagRequest,
} from '@orderlymeds/measurements-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiFetch } from './ApiProvider';

// Query keys
export const flagKeys = {
	all: (projectId: string) => ['projects', projectId, 'flags'] as const,
	detail: (projectId: string, flagId: string) => ['projects', projectId, 'flags', flagId] as const,
};

export function useFlags(projectId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: flagKeys.all(projectId),
		queryFn: () => apiFetch<FlagDoc[]>(`/api/admin/projects/${projectId}/flags`),
		enabled: !!projectId,
		staleTime: 30_000,
	});
}

export function useFlag(projectId: string, flagId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: flagKeys.detail(projectId, flagId),
		queryFn: () => apiFetch<FlagDoc>(`/api/admin/projects/${projectId}/flags/${flagId}`),
		enabled: !!projectId && !!flagId,
		staleTime: 30_000,
	});
}

export function useCreateFlag(projectId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateFlagRequest) =>
			apiFetch<FlagDoc>(`/api/admin/projects/${projectId}/flags`, {
				method: 'POST',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: flagKeys.all(projectId) });
		},
	});
}

export function useUpdateFlag(projectId: string, flagId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateFlagRequest) =>
			apiFetch<FlagDoc>(`/api/admin/projects/${projectId}/flags/${flagId}`, {
				method: 'PUT',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: flagKeys.all(projectId) });
			queryClient.invalidateQueries({ queryKey: flagKeys.detail(projectId, flagId) });
		},
	});
}

export function useChangeFlagStatus(projectId: string, flagId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (status: FlagStatus) =>
			apiFetch<void>(`/api/admin/projects/${projectId}/flags/${flagId}/status`, {
				method: 'PATCH',
				body: JSON.stringify({ status }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: flagKeys.all(projectId) });
			queryClient.invalidateQueries({ queryKey: flagKeys.detail(projectId, flagId) });
		},
	});
}
